/**
 * Centralized damage calculation pipeline.
 * Handles armor reduction and future modifiers (vulnerability, resistance, etc.)
 */

export type DamageTarget = {
  isDefending: boolean;
  armor: number;
};

export type DamageResult = {
  /** Final damage after all reductions */
  finalDamage: number;
  /** Amount absorbed by armor/block */
  absorbed: number;
  /** Whether target was defending */
  wasDefending: boolean;
};

/**
 * Calculate final damage after applying defensive modifiers.
 *
 * Current modifiers:
 * - Armor reduction (only when defending)
 *
 * Future extensibility:
 * - Block (temporary shield that gets consumed)
 * - Vulnerability (increased damage taken)
 * - Resistance (percentage reduction)
 */
export const calculateDamage = (rawDamage: number, target: DamageTarget): DamageResult => {
  let finalDamage = rawDamage;
  let absorbed = 0;
  const wasDefending = target.isDefending;

  // Apply armor reduction only when defending
  if (target.isDefending && target.armor > 0) {
    absorbed = Math.min(target.armor, rawDamage);
    finalDamage = Math.max(0, rawDamage - target.armor);
  }

  return {
    finalDamage,
    absorbed,
    wasDefending,
  };
};
